<!--
  @component DomainScreen — this deployment's domain.
  tier:      screen (per-project — `/projects/:id/domain`)
  scope:     the DEPLOYMENT side of domains — point a domain at this
             app (bring your own + manage routing DNS) and a light
             search-and-buy. The full registrar storefront (portfolio,
             transfers, bulk WHOIS) is a future sidebar product.
  reads:     useDeploymentIO  → dns.records.* (REAL Cloudflare) +
                                registrar.search/list/contacts/purchase (REAL)
             useIdentityIO    → project + productionDomain
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useValidationIO } from "../../../../global/io/nodes/security/ValidationIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import Skeleton from "../../../../global/visuals/primitives/Skeleton.svelte";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const validation = useValidationIO();

  const project = $derived(identity.currentProject);
  const liveUrl = $derived(project?.productionDomain ? `https://${project.productionDomain}` : null);

  // ── DNS records (REAL Cloudflare via dns.records.*) ─────────────
  interface DnsRecord { id: string; type: string; name: string; content: string; ttl: number; proxied: boolean; }
  interface DnsRecordsResult { zoneId?: string; records?: readonly DnsRecord[]; note?: string; }
  let dnsLoading = $state(false);
  let dnsError = $state<string | null>(null);
  let dnsResult = $state<DnsRecordsResult | null>(null);
  let draft = $state<{ type: string; name: string; content: string; ttl: number; proxied: boolean; id: string | null }>({ type: "A", name: "", content: "", ttl: 1, proxied: false, id: null });
  const contentCheck = $derived(validation.check(draft.content, "html"));
  const hasZone = $derived(Boolean(dnsResult?.zoneId));

  async function refreshDns(): Promise<void> {
    if (!project) return;
    dnsLoading = true; dnsError = null;
    try { dnsResult = (await node.dispatch("dns.records.list", { target: { id: project.id } })) as DnsRecordsResult; }
    catch (e) { dnsError = e instanceof Error ? e.message : String(e); dnsResult = null; }
    finally { dnsLoading = false; }
  }
  async function saveRecord(): Promise<void> {
    if (!project || !draft.type || !draft.name || !draft.content) return;
    dnsError = null;
    try {
      await node.dispatch("dns.records.set", { target: { id: project.id }, record: { ...draft, id: draft.id ?? undefined } });
      draft = { type: "A", name: "", content: "", ttl: 1, proxied: false, id: null };
      await refreshDns();
    } catch (e) { dnsError = e instanceof Error ? e.message : String(e); }
  }
  async function deleteRecord(r: DnsRecord): Promise<void> {
    if (!project) return;
    try { await node.dispatch("dns.records.delete", { target: { id: project.id }, record: { id: r.id } }); await refreshDns(); }
    catch (e) { dnsError = e instanceof Error ? e.message : String(e); }
  }
  function editRecord(r: DnsRecord): void { draft = { type: r.type, name: r.name, content: r.content, ttl: r.ttl, proxied: r.proxied, id: r.id }; }
  $effect(() => { if (project) void refreshDns(); });

  // ── Find a domain (REAL registrar.search + purchase) ────────────
  interface PriceQuote { registrationCents: number; renewalCents: number; currency: string; periodYears: number; registrarName: string; }
  interface Partner { name: string; url: string; label: string; }
  interface SearchResult { query: string; normalizedDomain: string | null; available: boolean | null; priceQuote: PriceQuote | null; partners: readonly Partner[]; }
  interface ContactRow { contactId: string; firstName: string; lastName: string; email: string; isDefault: boolean; }
  let q = $state("");
  let searching = $state(false);
  let searchError = $state<string | null>(null);
  let result = $state<SearchResult | null>(null);
  let contacts = $state<readonly ContactRow[]>([]);

  async function search(): Promise<void> {
    if (!q.trim()) return;
    searching = true; searchError = null;
    try { result = (await node.dispatch("registrar.search", { query: q.trim() })) as SearchResult; }
    catch (e) { searchError = e instanceof Error ? e.message : String(e); result = null; }
    finally { searching = false; }
  }
  async function refreshContacts(): Promise<void> {
    try { const r = (await node.dispatch("registrar.contacts.list", {})) as { contacts: readonly ContactRow[] }; contacts = r.contacts; }
    catch { /* non-fatal */ }
  }
  $effect(() => { void refreshContacts(); });

  // ── Light purchase ──────────────────────────────────────────────
  let buyOpen = $state(false);
  let buyDomain = $state<string | null>(null);
  let buyQuote = $state<PriceQuote | null>(null);
  let buyYears = $state(1);
  let buyAutoRenew = $state(true);
  let buying = $state(false);
  let buyError = $state<string | null>(null);
  let contact = $state({ firstName: "", lastName: "", organization: "", email: "", phone: "", addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "", country: "US" });
  const defaultContact = $derived(contacts.find((c) => c.isDefault) ?? contacts[0] ?? null);
  const total = $derived(buyQuote ? buyQuote.registrationCents * buyYears : 0);
  const dollars = (c: number) => `$${(c / 100).toFixed(2)}`;

  function openBuy(domain: string, quote: PriceQuote): void {
    buyDomain = domain; buyQuote = quote; buyYears = 1; buyAutoRenew = true; buyError = null; buyOpen = true;
  }
  const contactValid = $derived(
    Boolean(defaultContact) || (
      contact.firstName.trim() && contact.lastName.trim() && contact.email.includes("@") &&
      contact.phone.trim() && contact.addressLine1.trim() && contact.city.trim() &&
      contact.state.trim() && contact.postalCode.trim() && contact.country.trim().length === 2
    ),
  );
  async function confirmBuy(): Promise<void> {
    if (!buyDomain || !buyQuote) return;
    buying = true; buyError = null;
    try {
      const payload: Record<string, unknown> = { domain: buyDomain, periodYears: buyYears, autoRenew: buyAutoRenew };
      if (defaultContact) payload.contactId = defaultContact.contactId;
      else payload.contact = { ...contact, organization: contact.organization.trim() || null, addressLine2: contact.addressLine2.trim() || null, country: contact.country.trim().toUpperCase(), isDefault: true };
      await node.dispatch("registrar.purchase", payload);
      buyOpen = false; result = null; q = "";
      await refreshContacts();
    } catch (e) { buyError = e instanceof Error ? e.message : String(e); }
    finally { buying = false; }
  }

  // ── BYO connect ─────────────────────────────────────────────────
  let byo = $state("");
  let byoSubmitted = $state<string | null>(null);
  const byoCheck = $derived(validation.check(byo, "html"));
  function connectByo(): void { if (byo.trim() && byoCheck.ok) byoSubmitted = byo.trim(); }
</script>

<div class="screen">
  <header class="head">
    <h1>Domain</h1>
    <p class="sub">Give your app a home on the web — bring a domain you own, or grab a new one.</p>
  </header>

  <!-- Production domain -->
  <Card elevated eyebrow="Production" title="Your domain">
    {#if project?.productionDomain}
      <div class="prod">
        <div class="prod-id">
          <span class="prod-domain">{project.productionDomain}</span>
          <span class="prod-status"><span class="dot live"></span>Connected</span>
        </div>
        <a class="btn-secondary" href={liveUrl} target="_blank" rel="noopener">Open site</a>
      </div>
      <StatusNote tone="pending" message="Automatic HTTPS renews itself once the deploy connection is live." />
    {:else}
      <p class="muted">No domain connected yet. Connect one you own below, or find a new one.</p>
    {/if}
  </Card>

  <!-- Bring your own -->
  <Card eyebrow="Connect" title="Bring a domain you own">
    <div class="row">
      <input class="input" placeholder="yourdomain.com" bind:value={byo} spellcheck="false" />
      <button type="button" class="btn-primary" disabled={!byo.trim() || !byoCheck.ok} onclick={connectByo}>Connect</button>
    </div>
    {#if byoSubmitted}
      <div class="byo-steps">
        <p class="byo-title">Point <strong>{byoSubmitted}</strong> at your app</p>
        <p class="byo-help">Add one of these records at your domain's DNS provider:</p>
        <ul class="byo-records">
          <li><code>CNAME</code><code>{byoSubmitted}</code><span>→</span><code>your-app.liquidefy.app</code></li>
          <li><code>A</code><code>{byoSubmitted}</code><span>→</span><code>the deployment IP (shown after connect)</code></li>
        </ul>
        <StatusNote tone="pending" message="Once the records resolve, we verify ownership and issue HTTPS automatically — this turns on with the deploy connection." />
      </div>
    {/if}
  </Card>

  <!-- DNS records -->
  <Card eyebrow="Routing" title="DNS records">
    {#snippet actions()}
      {#if hasZone}<button type="button" class="link-action" onclick={() => void refreshDns()}>Refresh</button>{/if}
    {/snippet}
    {#if dnsLoading}
      <Skeleton variant="text" rows={3} />
    {:else if !hasZone}
      <p class="muted">Connect a domain to manage its DNS records here.</p>
    {:else}
      {#if dnsResult?.records && dnsResult.records.length > 0}
        <div class="dns-table">
          <div class="dns-head"><span>Type</span><span>Name</span><span>Value</span><span></span></div>
          {#each dnsResult.records as r (r.id)}
            <div class="dns-row">
              <code class="dns-type">{r.type}</code>
              <code class="dns-name">{r.name}</code>
              <code class="dns-val">{r.content}</code>
              <span class="dns-actions">
                <button type="button" onclick={() => editRecord(r)}>Edit</button>
                <button type="button" class="danger" onclick={() => void deleteRecord(r)}>Delete</button>
              </span>
            </div>
          {/each}
        </div>
      {:else}
        <p class="muted">No records yet.</p>
      {/if}
      <!-- inline add/edit -->
      <div class="dns-edit">
        <select class="input sel" bind:value={draft.type}>
          {#each ["A", "AAAA", "CNAME", "TXT", "MX"] as t (t)}<option value={t}>{t}</option>{/each}
        </select>
        <input class="input" placeholder="name" bind:value={draft.name} spellcheck="false" />
        <input class="input mono" placeholder="value" bind:value={draft.content} spellcheck="false" />
        <button type="button" class="btn-secondary" disabled={!draft.name || !draft.content || !contentCheck.ok} onclick={() => void saveRecord()}>
          {draft.id ? "Update" : "Add"}
        </button>
      </div>
      {#if dnsError}<p class="err">{dnsError}</p>{/if}
    {/if}
  </Card>

  <!-- Find a domain -->
  <Card eyebrow="Get a domain" title="Find a domain">
    <div class="row">
      <input class="input" placeholder="search for a domain…" bind:value={q} onkeydown={(e) => e.key === "Enter" && void search()} spellcheck="false" />
      <button type="button" class="btn-primary" disabled={!q.trim() || searching} onclick={() => void search()}>{searching ? "Searching…" : "Search"}</button>
    </div>
    {#if searchError}<p class="err">{searchError}</p>{/if}
    {#if result}
      <div class="result">
        {#if result.available && result.priceQuote}
          <div class="result-row available">
            <div><span class="result-domain">{result.normalizedDomain}</span><span class="tag avail">Available</span></div>
            <div class="result-buy">
              <span class="price">{dollars(result.priceQuote.registrationCents)}<span class="per">/yr</span></span>
              <button type="button" class="btn-primary" onclick={() => openBuy(result!.normalizedDomain!, result!.priceQuote!)}>Buy</button>
            </div>
          </div>
        {:else if result.available === false}
          <div class="result-row">
            <div><span class="result-domain">{result.normalizedDomain}</span><span class="tag taken">Taken</span></div>
          </div>
        {/if}
        {#if result.partners.length > 0}
          <p class="partners-label">Or search registrars:</p>
          <div class="partners">
            {#each result.partners as p (p.name)}<a class="partner" href={p.url} target="_blank" rel="noopener">{p.label}</a>{/each}
          </div>
        {/if}
      </div>
    {/if}
  </Card>
</div>

<!-- Light purchase modal -->
{#if buyOpen && buyDomain && buyQuote}
  <div class="scrim" role="presentation" onclick={() => (buyOpen = false)}></div>
  <div class="modal" role="dialog" aria-modal="true" aria-label="Buy domain">
    <h2>Buy {buyDomain}</h2>
    <div class="buy-rows">
      <label class="buy-field"><span>Years</span>
        <select class="input sel" bind:value={buyYears}>{#each [1,2,3,5] as y (y)}<option value={y}>{y}</option>{/each}</select>
      </label>
      <label class="buy-check"><input type="checkbox" bind:checked={buyAutoRenew} /> Auto-renew</label>
    </div>
    {#if !defaultContact}
      <p class="buy-help">Registrant details (required by ICANN):</p>
      <div class="contact-grid">
        <input class="input" placeholder="First name" bind:value={contact.firstName} />
        <input class="input" placeholder="Last name" bind:value={contact.lastName} />
        <input class="input" placeholder="Email" bind:value={contact.email} />
        <input class="input" placeholder="Phone" bind:value={contact.phone} />
        <input class="input span2" placeholder="Address" bind:value={contact.addressLine1} />
        <input class="input" placeholder="City" bind:value={contact.city} />
        <input class="input" placeholder="State" bind:value={contact.state} />
        <input class="input" placeholder="Postal code" bind:value={contact.postalCode} />
        <input class="input" placeholder="Country (US)" bind:value={contact.country} maxlength="2" />
      </div>
    {/if}
    {#if buyError}<p class="err">{buyError}</p>{/if}
    <div class="buy-foot">
      <span class="buy-total">{dollars(total)}<span class="per"> total</span></span>
      <div class="buy-actions">
        <button type="button" class="btn-secondary" onclick={() => (buyOpen = false)}>Cancel</button>
        <button type="button" class="btn-primary" disabled={buying || !contactValid} onclick={() => void confirmBuy()}>{buying ? "Purchasing…" : "Confirm purchase"}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 880px; font-family: var(--type-font); }
  .head h1 { margin: 0 0 var(--space-2); font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .sub { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
  .muted { margin: 0; font-size: 13px; color: var(--text-tertiary); line-height: 1.6; }

  .prod { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); margin-bottom: var(--space-3); }
  .prod-id { display: flex; align-items: center; gap: var(--space-3); }
  .prod-domain { font-size: 16px; font-weight: 500; color: var(--text-primary); }
  .prod-status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--success); }
  .dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-tertiary); }
  .dot.live { background: var(--success); }

  .row { display: flex; gap: var(--space-2); }
  .input { flex: 1; min-width: 0; padding: 9px var(--space-3); border-radius: var(--radius-md); border: 0.5px solid var(--border-input); background: var(--surface-sunken); color: var(--text-primary); font: inherit; font-size: 13.5px; }
  .input:focus { outline: none; border-color: var(--border-focus); }
  .input.mono { font-family: var(--type-mono); font-size: 12px; }
  .input.sel { flex: 0 0 auto; }
  .err { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--error); }

  .byo-steps { margin-top: var(--space-4); display: flex; flex-direction: column; gap: var(--space-2); }
  .byo-title { margin: 0; font-size: 13.5px; color: var(--text-primary); }
  .byo-help { margin: 0; font-size: 12.5px; color: var(--text-secondary); }
  .byo-records { list-style: none; margin: var(--space-1) 0 var(--space-2); padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
  .byo-records li { display: flex; align-items: center; gap: var(--space-2); }
  .byo-records code { font-family: var(--type-mono); font-size: 11.5px; color: var(--text-secondary); background: var(--bg-hover); padding: 2px var(--space-2); border-radius: var(--radius-xs); }
  .byo-records span { color: var(--text-ghost); }

  .dns-table { display: flex; flex-direction: column; margin-bottom: var(--space-3); }
  .dns-head, .dns-row { display: grid; grid-template-columns: 64px 1fr 1.6fr auto; gap: var(--space-3); align-items: center; padding: var(--space-2) 0; }
  .dns-head { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-tertiary); border-bottom: 0.5px solid var(--border-subtle); }
  .dns-row { border-bottom: 0.5px solid var(--border-subtle); }
  .dns-type, .dns-name, .dns-val { font-family: var(--type-mono); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dns-type { color: var(--text-tertiary); }
  .dns-name { color: var(--text-primary); }
  .dns-val { color: var(--text-secondary); }
  .dns-actions { display: flex; gap: var(--space-2); }
  .dns-actions button { background: transparent; border: 0; color: var(--text-tertiary); font: inherit; font-size: 11.5px; cursor: pointer; }
  .dns-actions button:hover { color: var(--text-primary); }
  .dns-actions button.danger:hover { color: var(--error); }
  .dns-edit { display: grid; grid-template-columns: auto 1fr 1.6fr auto; gap: var(--space-2); margin-top: var(--space-2); }

  .result { margin-top: var(--space-4); display: flex; flex-direction: column; gap: var(--space-3); }
  .result-row { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: var(--space-3); border: 0.5px solid var(--border); border-radius: var(--radius-md); background: var(--glass-fill); }
  .result-row.available { border-color: var(--success-subtle); }
  .result-domain { font-size: 14.5px; font-weight: 500; color: var(--text-primary); margin-right: var(--space-2); }
  .tag { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em; padding: 2px var(--space-2); border-radius: var(--radius-pill); }
  .tag.avail { color: var(--success); background: var(--success-subtle); }
  .tag.taken { color: var(--text-tertiary); background: var(--bg-hover); }
  .result-buy { display: flex; align-items: center; gap: var(--space-3); }
  .price { font-size: 15px; font-weight: 600; color: var(--text-primary); }
  .per { font-size: 12px; font-weight: 400; color: var(--text-tertiary); }
  .partners-label { margin: 0; font-size: 12px; color: var(--text-tertiary); }
  .partners { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  .partner { font-size: 12.5px; color: var(--text-secondary); border: 0.5px solid var(--border); border-radius: var(--radius-sm); padding: 4px var(--space-3); text-decoration: none; }
  .partner:hover { color: var(--text-primary); background: var(--bg-hover); }

  .link-action { background: transparent; border: 0; color: var(--text-secondary); font: inherit; font-size: 12.5px; cursor: pointer; }
  .link-action:hover { color: var(--text-primary); }

  .btn-primary, .btn-secondary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 38px; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; text-decoration: none; display: inline-flex; align-items: center; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary { background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
  .btn-secondary { background: transparent; color: var(--text-secondary); border: 0.5px solid var(--border); }
  .btn-secondary:hover:not(:disabled) { background: var(--bg-hover); color: var(--text-primary); }

  .scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(2px); z-index: 200; }
  .modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: min(460px, 92vw); max-height: 86vh; overflow-y: auto; background: var(--surface-raised); border: 0.5px solid var(--border); border-radius: var(--radius-xl); box-shadow: var(--shadow-lg); padding: var(--space-6); z-index: 201; font-family: var(--type-font); }
  .modal h2 { margin: 0 0 var(--space-4); font-size: 17px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .buy-rows { display: flex; align-items: center; gap: var(--space-5); margin-bottom: var(--space-4); }
  .buy-field { display: flex; flex-direction: column; gap: var(--space-1); font-size: 12px; color: var(--text-tertiary); }
  .buy-check { display: flex; align-items: center; gap: var(--space-2); font-size: 13px; color: var(--text-secondary); align-self: flex-end; }
  .buy-help { margin: 0 0 var(--space-2); font-size: 12.5px; color: var(--text-secondary); }
  .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-2); margin-bottom: var(--space-3); }
  .contact-grid .span2 { grid-column: 1 / -1; }
  .buy-foot { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-top: var(--space-4); padding-top: var(--space-3); border-top: 0.5px solid var(--border-subtle); }
  .buy-total { font-size: 16px; font-weight: 600; color: var(--text-primary); }
  .buy-actions { display: flex; gap: var(--space-2); }
</style>
